import React, { useEffect, useState } from 'react';

import _ from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Checkbox from '@material-ui/core/Checkbox';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';

import ConfirmButton from '../../components/ConfirmButton';

import classes from './AgentNetworkPanel.module.css';

const ValidationSchema = Yup.object().shape({
    basAgentInterface:   Yup.string(),
    // eslint-disable-next-line no-useless-escape
    stationIp:           Yup.string().matches(/^[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm, 'Invalid IP Address').required(),
    stationPort:         Yup.number().typeError('Please input a number').required(),
    obixLobbyPath:       Yup.string().required(),
    stationUsername:     Yup.string().required(),
    stationPassword:     Yup.string().required(),
    authType:            Yup.string().required(),
    isTrusted:           Yup.boolean(),
    batchPollTime:       Yup.number(),
    batchReadSize:       Yup.number(),
    maxConcTasks:        Yup.number(),
    requireHttps:        Yup.boolean(),
    requireCaValidation: Yup.boolean(),
    requireTLS:          Yup.boolean(),
    useCustomCa:         Yup.boolean(),
    customCa:            Yup.string()
});

const DEFAULT_FORM_VALUES = {
    basAgentInterface:   '127.0.0.1',
    stationIp:           '',
    stationPort:         '',
    obixLobbyPath:       'obix',
    stationUsername:     '',
    stationPassword:     '',
    authType:            'Niagara4Digest',
    isTrusted:           false,
    batchPollTime:       50,
    batchReadSize:       100,
    maxConcTasks:        4,
    requireHttps:        false,
    requireCaValidation: false,
    requireTLS:          false,
    useCustomCa:         false,
    customCa:            ''
};

const NiagaraPanel = ({
    save,
    agent,
    isLoading,
    ...props
}) => {
    const [initialValues, setInitialValues] = useState({...DEFAULT_FORM_VALUES});

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values) => {
            const payload = {
                basType:    'Niagara',
                connection: {
                    batchPollTime:       values.batchPollTime,
                    batchReadSize:       values.batchReadSize,
                    host:                values.basAgentInterface,
                    maxConcurrentTasks:  values.maxConcTasks,
                    niagaraAuthType:     values.authType,
                    oBIXLobby:           values.obixLobbyPath,
                    password:            values.stationPassword,
                    stationIp:           values.stationIp,
                    stationPort:         values.stationPort,
                    trustedDevice:       values.isTrusted,
                    userName:            values.stationUsername,
                    requireHttps:        values.requireHttps,
                    requireCaValidation: values.requireCaValidation,
                    requireTLS:          values.requireTLS,
                    useCustomCa:         values.useCustomCa,
                    customCa:            values.customCa
                }
            };
            let driverConfig = _.find(agent.driverConfiguration, driver => driver.basType === 'Niagara');
    
            if (driverConfig) {
                payload.id = driverConfig.id;
            } else {
                driverConfig = _.find(agent.driverConfiguration, driver => driver.basType === 'NotConfigured');
    
                if (driverConfig) payload.id = driverConfig.id;
            }
            save(payload);
        }
    });

    useEffect(() => {

        if (agent) {
            const driverConfig = _.find(agent.driverConfiguration, driver => driver.basType === 'Niagara');

            if (_.isNil(driverConfig)) return;

            const connection = _.get(driverConfig, 'connection', {});

            if (_.isEmpty(connection)) return;

            const formObject = {...DEFAULT_FORM_VALUES};

            formObject.obixLobbyPath = _.get(connection, 'oBIXLobby', '');
            formObject.authType = _.get(connection, 'niagaraAuthType', '');
            formObject.stationIp = _.get(connection, 'stationIp', '');
            formObject.stationPort = _.get(connection, 'stationPort', '');
            formObject.batchPollTime = _.get(connection, 'batchPollTime', 50);
            formObject.batchReadSize = _.get(connection, 'batchReadSize', 100);
            formObject.basAgentInterface = _.get(connection, 'host');
            formObject.maxConcTasks = _.get(connection, 'maxConcurrentTasks', 4);
            formObject.requireHttps = _.get(connection, 'requireHttps', false);
            formObject.requireCaValidation = _.get(connection, 'requireCaValidation', false);
            formObject.requireTLS = _.get(connection, 'requireTLS', false);
            formObject.useCustomCa = _.get(connection, 'useCustomCa', false);
            formObject.customCa = _.get(connection, 'customCa', ' ') || ' ';
    
            setInitialValues(formObject);
        }
    }, []);

    const showTooltipMessage = formState => {
        return formState ? `Click to Save settings.` : `You must check 'This is a device I trust' checkbox to save.`;
    };

    return (
        <div className={classes.NetworkPanel}>
            <div className={classes.formContainer}>
                <div className={classes.formColumn}>
                    <div className={classes.labelColumn}>
                        <div className={classes.topNiagaraLabelGroup}>
                            <div className={classes.labelHeader}>
                                <img src="/assets/images/generalNetworkSettingsIconColor.svg" alt="settings icon" />
                                <span>Niagara Network Settings</span>
                            </div>
                            <div className={classes.labelContent}>
                                <p>
                                    The Niagara Settings provide the BAS Agent with the Niagara Station IP, the associated oBIX lobby, and the authentication details required to communicate with the Niagara system.
                                </p>
                                <p>
                                    Please ensure that the host you are connecting to is one you trust. If you do not trust the host, please <strong>DO NOT</strong> check this box as BOB will download any certificates presented by the host to allow a secure connection to be made between the host and the BAS Agent.
                                </p>
                            </div>
                        </div>
                        <div>
                            <div className={classes.labelHeader}>
                                <img src="/assets/images/lockFlatOutline.svg" alt="credentials icon" />
                                <span>Niagara Credentials</span>
                            </div>
                            <div className={`${classes.labelContent} ${classes.bottomPadding}`}>
                                <p>
                                    The user name and password for the Niagara connection are stored securely in the cloud. Note that credentials are read only and are not available for access after saving.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={classes.inputColumn}>
                        <div className={`${classes.inputs} ${classes.topNiagaraInputGroup}`}>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="stationIp"
                                    classes={{
                                        focused: classes.cssFocused
                                    }}
                                    error={Boolean(formik.errors.stationIp) && Boolean(formik.touched.stationIp)}
                                >
                                    Niagara Station IP Address
                                </InputLabel>
                                <Input
                                    id="stationIp"
                                    name="stationIp"
                                    value={formik.values.stationIp}
                                    onChange={formik.handleChange}
                                    classes={{
                                        underline: classes.cssUnderline
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.stationIp) && Boolean(formik.touched.stationIp)}
                                />
                                {formik.touched.stationIp &&
                                    formik.errors.stationIp && <FormHelperText className={classes.error}>{formik.errors.stationIp}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="stationPort"
                                    classes={{
                                        focused: classes.cssFocused
                                    }}
                                    error={Boolean(formik.errors.stationPort) && Boolean(formik.touched.stationPort)}
                                >
                                    Niagara HTTPS/HTTP Port
                                </InputLabel>
                                <Input
                                    id="stationPort"
                                    name="stationPort"
                                    value={formik.values.stationPort}
                                    onChange={formik.handleChange}
                                    classes={{
                                        underline: classes.cssUnderline
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.stationPort) && Boolean(formik.touched.stationPort)}
                                />
                                {formik.touched.stationPort &&
                                    formik.errors.stationPort && <FormHelperText className={classes.error}>{formik.errors.stationPort}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="obixLobbyPath"
                                    classes={{
                                        focused: classes.cssFocused
                                    }}
                                    error={Boolean(formik.errors.obixLobbyPath) && Boolean(formik.touched.obixLobbyPath)}
                                >
                                    oBIX Lobby Path
                                </InputLabel>
                                <Input
                                    id="obixLobbyPath"
                                    name="obixLobbyPath"
                                    value={formik.values.obixLobbyPath}
                                    onChange={formik.handleChange}
                                    classes={{
                                        underline: classes.cssUnderline
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.obixLobbyPath) && Boolean(formik.touched.obixLobbyPath)}
                                />
                                {formik.touched.obixLobbyPath &&
                                    formik.errors.obixLobbyPath && <FormHelperText className={classes.error}>{formik.errors.obixLobbyPath}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="authType"
                                    classes={{
                                        focused: classes.cssFocused
                                    }}
                                    error={Boolean(formik.errors.authType) && Boolean(formik.touched.authType)}
                                >
                                    Authentication Type
                                </InputLabel>
                                <Select
                                    value={formik.values.authType}
                                    onChange={formik.handleChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'authType',
                                        id:     'authType',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.authType) && Boolean(formik.touched.authType)}
                                >
                                    <MenuItem value="HttpBasic" className={classes.selectMenuItem}>Http Basic</MenuItem>
                                    <MenuItem value="NiagaraAXDigest" className={classes.selectMenuItem}>Niagara AX Digest</MenuItem>
                                    <MenuItem value="Niagara4Digest" className={classes.selectMenuItem}>Niagara 4 Digest</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl className={classes.checkboxWrapper}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.isTrusted}
                                            value="isTrusted"
                                            name="isTrusted"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={'This is a device I trust'}
                                    className={classes.checkboxLabel}
                                />
                            </FormControl>
                        </div>
                        <div className={classes.inputs}>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="stationUsername"
                                    classes={{
                                        focused: classes.cssFocused
                                    }}
                                    error={Boolean(formik.errors.stationUsername) && Boolean(formik.touched.stationUsername)}
                                >
                                    Station Username
                                </InputLabel>
                                <Input
                                    id="stationUsername"
                                    name="stationUsername"
                                    value={formik.values.stationUsername}
                                    onChange={formik.handleChange}
                                    classes={{
                                        underline: classes.cssUnderline
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.stationUsername) && Boolean(formik.touched.stationUsername)}
                                />
                                {formik.touched.stationUsername &&
                                    formik.errors.stationUsername && <FormHelperText className={classes.error}>{formik.errors.stationUsername}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="stationPassword"
                                    classes={{
                                        focused: classes.cssFocused
                                    }}
                                    error={Boolean(formik.errors.stationPassword) && Boolean(formik.touched.stationPassword)}
                                >
                                    Station Password
                                </InputLabel>
                                <Input
                                    id="stationPassword"
                                    name="stationPassword"
                                    type="password"
                                    value={formik.values.stationPassword}
                                    onChange={formik.handleChange}
                                    classes={{
                                        underline: classes.cssUnderline
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.stationPassword) && Boolean(formik.touched.stationPassword)}
                                />
                                {formik.touched.stationPassword &&
                                    formik.errors.stationPassword && <FormHelperText className={classes.error}>{formik.errors.stationPassword}</FormHelperText>}
                            </FormControl>
                        </div>
                    </div>
                </div>
                <div className={classes.formColumn}>
                    <div className={classes.labelColumn}>
                        <div className={classes.topNiagaraLabelGroup}>
                            <div className={classes.labelHeader}>
                                <img src="/assets/images/generalNetworkSettingsIconColor.svg" alt="settings icon" />
                                <span>Advanced Security Settings</span>
                            </div>
                            <div className={classes.labelContent}>
                                <p>
                                    <strong>IMPORTANT</strong> Key2Act strongly recommends only using TLS v1.2+ to connect to the Niagara system. Lower versions of TLS or SSL as well as unsecured HTTP connections can lead to system vulnerability. Key2Act assumes no responsibility for use of insecure methods of data access.
                                </p>
                                <p>
                                    It is best practice to require certificate validation. However, if the system is using a self-signed certificate, this may not be possible. If you are confident that the self-signed certificate is valid, or if there is no certificate in the case of HTTP access, this may be unchecked to allow access. Key2Act always advises users to validate certificates.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={classes.advancedSecurityInputColumn}>
                        <div className={`${classes.inputs} ${classes.topNiagaraInputGroup}`}>                                
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.requireHttps}
                                            value="requireHttps"
                                            name="requireHttps"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={'Require HTTPS'}
                                    className={classes.checkboxLabel}
                                />
                                {
                                    !formik.values.requireHttps &&
                                        <FormHelperText className={classes.warning}><ErrorOutlineIcon className={classes.alignWarningIcon} /> {'WARNING - Disabling HTTPs requirement removes encryption and may lead to exposed data. Key2Act only recommends HTTPS.'}</FormHelperText>
                                }   
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.requireCaValidation}
                                            value="requireCaValidation"
                                            name="requireCaValidation"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={'Require Certificate Validation'}
                                    className={classes.checkboxLabel}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.requireTLS}
                                            value="requireTLS"
                                            name="requireTLS"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={'Require Secure protocols (TLS1.2+)'}
                                    className={classes.checkboxLabel}
                                />
                                {
                                    !formik.values.requireTLS &&
                                        <FormHelperText className={classes.warning}><ErrorOutlineIcon className={classes.alignWarningIcon} /> {'WARNING - Protocols <TLS1.2 have known vulnerabilities that can be exploited and may lead to exposed data. Key2Act only recommends using TLS1.2+'}</FormHelperText>
                                }
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.useCustomCa}
                                            value="useCustomCa"
                                            name="useCustomCa"
                                            color="primary"
                                            onChange={formik.handleChange}
                                        />
                                    }
                                    label={'Use Custom Certificate Authority (CA)'}
                                    className={classes.checkboxLabel}
                                />
                                {
                                    formik.values.useCustomCa &&
                                        <TextField
                                            className={classes.customCaTextarea}
                                            name="customCa"
                                            label="CA Root Key"
                                            multiline
                                            rows={4}
                                            value={formik.values.customCa || ' '}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                        />
                                }
                            </FormControl>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.buttonContainer}>
                <Tooltip
                    classes={{ tooltip: classes.toolTip }}
                    title={showTooltipMessage(formik.values.isTrusted)}
                    placement="top"
                >
                    <div>
                        <ConfirmButton
                            label="Save"
                            type="button"
                            disabled={!formik.values.isTrusted}
                            isLoading={isLoading}
                            onClick={formik.handleSubmit}
                        />
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};

export default NiagaraPanel;
